/**
 * AgentActivityFeed - Vertical feed of NodeCards representing the processing plan.
 * Each card starts pending, becomes active, then done as SSE events arrive.
 * Replaces the separate ExecutionPlan component.
 */
import { useState, useEffect } from 'react';
import { NodeCard } from './NodeCard';

export function AgentActivityFeed({ stages, isComplete }) {
  const [feedExpanded, setFeedExpanded] = useState(true);

  // Auto-collapse when all stages are done
  useEffect(() => {
    if (isComplete) {
      const timer = setTimeout(() => setFeedExpanded(false), 1500);
      return () => clearTimeout(timer);
    }
  }, [isComplete]);

  // Re-expand when new active stages appear
  useEffect(() => {
    if (!isComplete && stages.some(s => s.status === 'active')) {
      setFeedExpanded(true);
    }
  }, [stages, isComplete]);

  if (!stages || stages.length === 0) return null;

  const doneCount = stages.filter(s => s.status === 'done').length;

  // Collapsed summary
  if (!feedExpanded) {
    return (
      <button
        onClick={() => setFeedExpanded(true)}
        className="mb-3 w-full flex items-center gap-2.5 px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50/80 text-left hover:bg-gray-100/60 transition-colors"
      >
        <svg className="w-3.5 h-3.5 text-gray-400 shrink-0" viewBox="0 0 8 12" fill="none">
          <path d="M1.5 1L6.5 6L1.5 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <svg className="w-4 h-4 text-emerald-500 shrink-0" viewBox="0 0 16 16" fill="none">
          <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.1" />
          <path d="M5 8.5L7 10.5L11 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <span className="text-[13px] font-medium text-gray-600">
          Completed ({doneCount} stage{doneCount !== 1 ? 's' : ''})
        </span>
      </button>
    );
  }

  return (
    <div className="mb-3">
      {stages.map((stage, i) => (
        <NodeCard
          key={`${stage.node}-${stage.id || i}`}
          node={stage.node}
          label={stage.label}
          status={stage.status}
          timestamp={stage.timestamp}
          data={stage.data}
        />
      ))}
    </div>
  );
}
